import React from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ADMIN } from "@/constants/testIds";
import {
  LayoutDashboard, Sparkles, Package, Users, Briefcase, Mail, FileText, Settings, LogOut, Key
} from "lucide-react";

const items = [
  { to: "/admin", key: "dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/site", key: "site", label: "Site Content", icon: Settings },
  { to: "/admin/services", key: "services", label: "Services", icon: Sparkles },
  { to: "/admin/products", key: "products", label: "Products", icon: Package },
  { to: "/admin/team", key: "team", label: "Team", icon: Users },
  { to: "/admin/jobs", key: "jobs", label: "Job Posts", icon: Briefcase },
  { to: "/admin/applications", key: "applications", label: "Applications", icon: FileText },
  { to: "/admin/messages", key: "messages", label: "Messages", icon: Mail },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="admin-shell flex">
      <aside className="w-64 shrink-0 border-r border-[color:var(--sky-admin-border)] p-5 hidden md:flex md:flex-col min-h-screen sticky top-0">
        <Link to="/admin" className="flex items-center gap-3">
          <img src="/skyverra-logo.png" alt="Skyverra" className="h-9 w-auto" />
          <div className="leading-tight">
            <div className="font-display font-bold text-white text-base">Skyverra</div>
            <div className="text-[10px] tracking-[0.22em] font-bold text-[color:var(--sky-cyan)]">ADMIN</div>
          </div>
        </Link>

        <nav className="mt-8 flex-1 flex flex-col gap-1">
          {items.map((it) => (
            <NavLink
              key={it.key}
              to={it.to}
              end={it.end}
              data-testid={ADMIN.sidebarLink(it.key)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-[color:var(--sky-cyan)]/15 text-white border border-[color:var(--sky-cyan)]/40"
                    : "text-[color:var(--sky-admin-muted)] hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <it.icon size={16} />
              <span>{it.label}</span>
            </NavLink>
          ))}

          <div className="my-3 border-t border-[color:var(--sky-admin-border)]" />
          <NavLink
            to="/admin/api-login"
            data-testid={ADMIN.apiLoginLink}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-[color:var(--sky-cyan)]/15 text-white border border-[color:var(--sky-cyan)]/40"
                  : "text-[color:var(--sky-admin-muted)] hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Key size={16} />
            <span>API Login</span>
            <span className="ml-auto text-[9px] tracking-widest font-bold text-[color:var(--sky-cyan)]">HIDDEN</span>
          </NavLink>
        </nav>

        <div className="mt-6 border-t border-[color:var(--sky-admin-border)] pt-4">
          <div className="text-xs text-[color:var(--sky-admin-muted)] break-all">{user?.email}</div>
          <button
            onClick={handleLogout}
            className="mt-3 admin-btn-ghost inline-flex items-center gap-2 text-sm"
            data-testid={ADMIN.logoutBtn}
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="border-b border-[color:var(--sky-admin-border)] px-6 py-4 flex items-center justify-between md:hidden">
          <div className="flex items-center gap-2">
            <img src="/skyverra-logo.png" className="h-7" alt="" />
            <span className="font-display font-bold text-white">Admin</span>
          </div>
          <button onClick={handleLogout} className="admin-btn-ghost text-xs" data-testid={`${ADMIN.logoutBtn}-mobile`}>Sign out</button>
        </div>
        <div className="p-6 md:p-10 max-w-6xl">
          {children}
        </div>
      </main>
    </div>
  );
}
